import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import { Modal, Checkbox, DatePicker, Icon, Input, Form, Alert, Radio } from "ant-design-vue";
import moment, { Moment } from "moment";
import { CommonLocale, RevisionMangerLocale } from "@/locales/localeid";
import { GridRef } from "@/components/wijmo-grid/grid/grid";
import Grid from "@/components/wijmo-grid/grid/grid.vue";
import { Selector } from "wijmo/wijmo.grid.selector";
import { fmtMsg, ActionItem, MessageHelper, NotificationType } from "commonui";
import PageTitle from "@/components/page-title/page-title.vue";
import { disabledDueToAccessLevel, getRouteAccessLevel } from "@/util/func";
import { VersionGroupModule } from "@/modules/versiongroup";
import {
	ContentMediaDetail,
	ContentMediaDetailForView,
	MediaNotesRequestModel,
	MediaPublishRequestModel,
	RevisionManagerService,
	RevisionDetailForView
} from "@/services/revision-manager";
import { ListStateStoreKeys, MediaSlotTypeMapForView, MediaStatusTypeMap } from "@/util/constants";
import { CellType } from "wijmo/wijmo.grid";
import { addClass } from "wijmo/wijmo";
import TitleListState from "@/modules/title-list";
import { getModule } from "vuex-module-decorators";
import { VCSLevelModel } from "@/models/title-list-model";
import { isAnyVersionSelected } from "@/util/func";
import { groupBy, orderBy } from "lodash";
import { CurriculumType, ListStates, MediaPublishOptionsEnum, MediaStatus, RevisionManagerFiltersEnum } from "@/util/enum";
import { ListStatesRadiosProps, ListStatesStore } from "@/components/list-states-radios/list-states-radios";

const listStateKey = "revision-manager";
const defaultListState = ListStates.Queued;

@Component({
	components: {
		PageTitle,
		Grid,
		Modal,
		Checkbox,
		DatePicker,
		Icon,
		Input,
		ActionItem,
		Form,
		FormItem: Form.Item,
		Alert,
		Radio,
		RadioGroup: Radio.Group
	}
})
export default class RevisionManager extends Vue {
	@Ref() readonly gridCompRef!: GridRef;
	public titleListModule = getModule(TitleListState);
	public revisionMangerLocale = RevisionMangerLocale;
	public gridLoading: boolean = false;
	public scheduleModalVisible: boolean = false;
	public moment = moment;
	public revisionModalVisible: boolean = false;
	public selector: Selector | null = null;
	public selectedItems: any[] = [];
	public versionGroupModule = VersionGroupModule;
	public publishOnSpecificDate: boolean = true;
	public scheduleDateValue: null | Moment = null;
	public medias: ContentMediaDetailForView[] = [];
	vcsLevels: VCSLevelModel[] = [];
	public modalLoading: boolean = false;
	public isDirty: boolean = false;
	public dateFormat: string = "MM/DD/YYYY";
	public dateFormatForSort: string = "YYYY/MM/DD";
	public filterStatusValue: RevisionManagerFiltersEnum = RevisionManagerFiltersEnum.Queued;
	public allCount: number = 0;
	public queuedCount: number = 0;
	public processingCount: number = 0;
	public scheduledCount: number = 0;
	public deletedCount: number = 0;
	public filteredMediasForView: ContentMediaDetailForView[] = [];
	public listStateStore: ListStatesStore = new ListStatesStore(ListStateStoreKeys.RevisionManager, listStateKey);
	public publishOptionValue: MediaPublishOptionsEnum = MediaPublishOptionsEnum.ScheduledPublish;

	revisionManagerService = RevisionManagerService();
	localizedString(id: string, params?: {}) {
		return fmtMsg(id, params);
	}

	public selectedComponent: ContentMediaDetailForView = {
		componentId: "",
		component: "",
		unit: 0,
		language: "",
		mediaRevisionDetails: [],
		revisionDate: "",
		status: "",
		scheduledDate: "",
		unitDisplayName: "",
		sortUnit: -1,
		revisionDateForSort: ""
	};

	mounted() {
		VersionGroupModule.setDisabled(false);
		this.vcsLevels = this.titleListModule.levels;
		this.getMedias();
		this.listStateStore.setState(ListStates.Queued);
	}

	destroyed() {
		VersionGroupModule.setDisabled(true);
	}

	@Watch("versionGroupModule.selectedLsVersion")
	@Watch("versionGroupModule.selectedGsVersion")
	onUpdateVersion() {
		if (isAnyVersionSelected()) {
			this.getMedias();
		}
	}

	get commonLocale(): CommonLocale {
		return CommonLocale;
	}

	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	get disabledDueToAccessLevel() {
		return disabledDueToAccessLevel(this.currentRouteAccessLevel);
	}

	getMedias() {
		this.gridLoading = true;
		this.revisionManagerService
			.getMedias({
				gsVersion: this.versionGroupModule.selectedGsVersion,
				lsVersion: this.versionGroupModule.selectedLsVersion
			})
			.then((mediasData) => {
				this.medias = mediasData.map((media) => {
					return {
						...media,
						status: MediaStatusTypeMap[media.status],
						unit: this.getVCSUnitNumber(media.unit),
						unitDisplayName: this.getVCSName(media.unit),
						revisionDate: media.revisionDate
							? moment(media.revisionDate).format(this.dateFormat).toString()
							: media.revisionDate,
						scheduledDate: media.scheduledDate
							? moment(media.scheduledDate).format(this.dateFormat).toString()
							: media.scheduledDate,
						mediaRevisionDetails: media.mediaRevisionDetails.map((rev) => {
							return { ...rev, mediaSlot: MediaSlotTypeMapForView[rev.mediaSlotId] };
						}),
						sortUnit:
							media.curriculumType == CurriculumType.LittleSEED ? media.unit - 1000 : media.unit + 1000,
						revisionDateForSort: media.revisionDate
							? moment(media.revisionDate).format(this.dateFormatForSort).toString()
							: null
					};
				});
				this.setFilteredData();
				this.gridLoading = false;
			})
			.catch(() => {
				this.gridLoading = false;
			});
	}

	getVCSName(id) {
		if (!id) {
			return "";
		}
		return this.vcsLevels.find((d) => d.id === id)?.displayName;
	}

	getVCSUnitNumber(id) {
		if (!id) {
			return 0;
		}
		return this.vcsLevels.find((d) => d.id === id)?.unit;
	}

	onScheduleClick() {
		this.scheduleModalVisible = true;
		const isSingleComponentSelected = this.selectedItems.length === 1;

		this.publishOptionValue = MediaPublishOptionsEnum.ScheduledPublish;
		this.publishOnSpecificDate = true;
		if (isSingleComponentSelected) {
			const selectedComponent = this.selectedItems[0]._data as ContentMediaDetail;
			if (!selectedComponent.scheduledDate) {
				this.scheduleDateValue = null;
			} else {
				this.scheduleDateValue = moment(selectedComponent.scheduledDate, this.dateFormat);
			}
		} else if (this.selectedItems.length > 1) {
			this.scheduleDateValue = null;
		}
	}

	handleScheduleModalPublish() {
		this.modalLoading = true;
		const isQueuedMediasPresent: boolean = this.selectedItems.some(
			(item) => item._data.status == MediaStatusTypeMap[MediaStatus.Queued]
		);

		if (
			isQueuedMediasPresent &&
			this.scheduleDateValue?.format(this.dateFormat) == null &&
			this.publishOptionValue === MediaPublishOptionsEnum.ScheduledPublish
		) {
			MessageHelper.Message(
				NotificationType.Warning,
				this.localizedString(RevisionMangerLocale.RevisionManagaerSchMdlMediaPublishWarning)
			);
			this.modalLoading = false;
		} else {
			const mediasToPublish: MediaPublishRequestModel = {
				mediaIds: this.selectedItems
					.map((item) => item._data.mediaRevisionDetails.map((mr) => mr.mediaId))
					.flat(),
				scheduleDate: this.scheduleDateValue?.format(this.dateFormat) as string
			};
			if (this.publishOptionValue === MediaPublishOptionsEnum.ScheduledPublish) {
				this.revisionManagerService
					.publishMedias(mediasToPublish)
					.then((result) => {
						if (result) {
							this.scheduleModalVisible = false;
							this.getMedias();
						}
						this.selectedItems = [];
						this.modalLoading = false;
						MessageHelper.Message(
							NotificationType.Success,
							this.localizedString(RevisionMangerLocale.RevisionManagaerSchMdlMediaScheduledText)
						);
					})
					.catch((err) => {
						this.modalLoading = false;
					});
			} else {
				// publish immediately api call
				mediasToPublish.scheduleDate = moment().format(this.dateFormat) as string;

				this.revisionManagerService
					.publishMediasImmediately(mediasToPublish)
					.then((result) => {
						if (result) {
							this.scheduleModalVisible = false;
							this.getMedias();
						}
						this.selectedItems = [];
						this.modalLoading = false;
						MessageHelper.Message(
							NotificationType.Success,
							this.localizedString(RevisionMangerLocale.RevisionManagaerSchMdlMediaPublishedText)
						);
					})
					.catch((err) => {
						this.modalLoading = false;
					});
			}
		}
	}

	handleCancel() {
		this.scheduleModalVisible = false;
		this.revisionModalVisible = false;
		this.scheduleDateValue = moment("00:00:00", "HH:mm:ss");
		this.publishOnSpecificDate = false;
		this.isDirty = false;
		this.modalLoading = false;
	}

	range(start, end) {
		const result: number[] = [];
		for (let i = start; i < end; i++) {
			result.push(i);
		}
		return result;
	}

	disabledDate(current) {
		// Can not select days before today and today
		return current && current < moment().endOf("day");
	}

	disabledDateTime() {
		return {
			disabledHours: () => this.range(0, 24).splice(4, 20),
			disabledMinutes: () => this.range(30, 60),
			disabledSeconds: () => [55, 56]
		};
	}

	viewRevisionDetail(revisionDetail) {
		this.revisionModalVisible = true;
		this.selectedComponent = revisionDetail;
	}

	initGrid(grid) {
		if (!this.disabledDueToAccessLevel) {
			this.selector = new Selector(grid, {
				itemChecked: () => {
					this.selectedItems = grid.rows.filter((r) => r.isSelected);
				},
				showCheckAll: true
			});

			grid.formatItem.addHandler((s, e) => {
				const currentListState = this.listStateStore.getState(listStateKey, defaultListState);

				if (
					e.panel.cellType === CellType.TopLeft &&
					(currentListState === ListStates.All || currentListState === ListStates.Processing)
				) {
					addClass(e.cell, "wj-state-disabled");
				}

				if (currentListState === ListStates.All && e.panel.cell === CellType.RowHeader) {
					addClass(e.cell, "wj-state-disabled");
					s.rows[e.row].isSelected = false;
					(e.cell.firstChild.firstChild as HTMLInputElement).checked = false;
				}

				if (e.panel.cellType == CellType.RowHeader) {
					const col = s.getColumn("status");
					const cellData = s.getCellData(e.row, col.index, false);
					if (currentListState === ListStates.All) {
						if (cellData) {
							addClass(e.cell, "wj-state-disabled");
							s.rows[e.row].isSelected = false;
							(e.cell.firstChild.firstChild as HTMLInputElement).checked = false;
						}
					} else {
						if (cellData && cellData === RevisionManagerFiltersEnum.Processing) {
							addClass(e.cell, "wj-state-disabled");
							s.rows[e.row].isSelected = false;
							(e.cell.firstChild.firstChild as HTMLInputElement).checked = false;
						}
					}
				}
			});
		}
	}

	onScheduleDateChange(date, dateString) {
		this.scheduleDateValue = date;
		this.isDirty = true;
	}

	get componentRevisions() {
		const data = orderBy(
			groupBy(this.selectedComponent.mediaRevisionDetails, "mediaSlotId"),
			["revision"],
			["desc"]
		);
		return data.map((x) => x.reduce((prev, current) => (prev.revision > current.revision ? prev : current)));
	}

	onRevisionNoteChange(e, mediaId: string) {
		this.isDirty = true;
		const media = this.selectedComponent.mediaRevisionDetails.find((m) => m.mediaId === mediaId);
		(media as RevisionDetailForView).note = e.target.value;
	}

	onRevisionModalSave() {
		this.modalLoading = true;
		const mediasNote: MediaNotesRequestModel[] = [];
		const component = this.medias.find((media) => media.componentId === this.selectedComponent.componentId);
		const componentMediaDetails = component?.mediaRevisionDetails;
		if (componentMediaDetails?.length) {
			componentMediaDetails.forEach((element) => {
				mediasNote.push({
					mediaId: element.mediaId,
					note: element.note
				});
			});

			this.revisionManagerService.saveMediasNote(mediasNote).then((result) => {
				if (result) {
					// save notes on ui
					const componentId = this.selectedComponent.componentId;
					const component = this.medias.find((media) => media.componentId === componentId);
					component?.mediaRevisionDetails.forEach((revDetail) => {
						revDetail.note = mediasNote.find((m) => m.mediaId === revDetail.mediaId)?.note as string;
					});
					this.modalLoading = false;
					this.revisionModalVisible = false;
					this.isDirty = false;
					MessageHelper.Message(
						NotificationType.Success,
						this.localizedString(RevisionMangerLocale.RevisionManagaerRevMdlMediaNotesSavedText)
					);
				}
			});
		}
	}

	get revisionManagerFilterEnum(): any {
		return RevisionManagerFiltersEnum;
	}

	get mediaPublishSelectionOptions(): any {
		return MediaPublishOptionsEnum;
	}

	onPublishOptionChange(e) {
		if (e.target.value === MediaPublishOptionsEnum.PublishImmediately) {
			this.publishOnSpecificDate = false;
			this.scheduleDateValue = null;
			this.isDirty = true;
		} else if (e.target.value === MediaPublishOptionsEnum.ScheduledPublish && !this.scheduleDateValue) {
			this.publishOnSpecificDate = true;
			this.isDirty = false;
		} else if (e.target.value === MediaPublishOptionsEnum.ScheduledPublish && this.scheduleDateValue) {
			this.publishOnSpecificDate = true;
			this.isDirty = true;
		}
	}

	get listStateOptions(): ListStatesRadiosProps {
		return {
			value: this.listStateStore.getState(listStateKey, defaultListState),
			allCnt: this.allCount,
			rmQueuedCount: this.queuedCount,
			rmProcessingCount: this.processingCount,
			rmScheduledCount: this.scheduledCount,
			rmDeletedCount: this.deletedCount,
			onChange: this.onListValueChange,
			showActiveRadio: false,
			showInActiveRadio: false
		};
	}

	onListValueChange(value: ListStates) {
		this.listStateStore.setState(value);
		this.setFilteredData();
		this.selectedItems = [];
	}

	setFilteredData() {
		const allMedias = this.medias;
		const queuedMedias = allMedias.filter((d) => d.status === RevisionManagerFiltersEnum.Queued);
		const processingMedias = allMedias.filter((d) => d.status === RevisionManagerFiltersEnum.Processing);
		const scheduledMedias = allMedias.filter((d) => d.status === RevisionManagerFiltersEnum.Scheduled);
		const deletedMedias = allMedias.filter((d) => d.status === RevisionManagerFiltersEnum.Published);

		this.allCount = allMedias.length;
		this.queuedCount = queuedMedias.length;
		this.processingCount = processingMedias.length;
		this.scheduledCount = scheduledMedias.length;
		this.deletedCount = deletedMedias.length;

		switch (this.listStateStore.getState(listStateKey, defaultListState)) {
			case ListStates.All:
				this.filteredMediasForView = allMedias;
				break;
			case ListStates.Queued:
				this.filteredMediasForView = queuedMedias;
				break;
			case ListStates.Processing:
				this.filteredMediasForView = processingMedias;
				break;
			case ListStates.Scheduled:
				this.filteredMediasForView = scheduledMedias;
				break;
			case ListStates.Deleted:
				this.filteredMediasForView = deletedMedias.map((dm) => {
					return { ...dm, status: RevisionManagerFiltersEnum.Deleted };
				});
				break;
			default:
		}
	}
}
